// NOTE: The intention for common.js is to consolidate logic that can be used inside of
// and outside of luminary. Anything in here should *not* use a luminary instance, but
// rather should be stand-alone.

import { AssetSuffix } from './enums';

export function shouldLoopMedia(slideDuration: number, mediaDuration: number, volume: number) {
    if (slideDuration === -1 || mediaDuration === -1) {
        console.warn(
            'Scene or background media has no duration. Unable to determine playback rate. Returning 1.',
        );
        return true;
    }

    const MIN_PLAYBACK_RATE = 0.6667;
    const slowdown_playback_rate = mediaDuration / slideDuration;
    if (slowdown_playback_rate < MIN_PLAYBACK_RATE) {
        return true;
    } else if (volume > 0 && mediaDuration < slideDuration) {
        return true;
    }

    return false;
}

/**
 * Determine the playback rate of a slide's media based on the slide duration, media duration and the media's
 * end time.
 */
export function getMediaPlaybackRate(
    slideDuration: number,
    mediaDuration: number,
    volume: number,
): number {
    if (shouldLoopMedia(slideDuration, mediaDuration, volume)) {
        return 1.0;
    }

    if (slideDuration > mediaDuration) {
        return mediaDuration / slideDuration;
    }

    return 1.0;
}

/**
 * Capitalizes the first letter of a string.
 */
export const capitalizeFirstLetter = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
};

export function hasOwnProperty<X extends {}, Y extends PropertyKey>(
    obj: X,
    prop: Y,
): obj is X & Record<Y, unknown> {
    return obj.hasOwnProperty(prop);
}

/**
 * Components of a name e.g. text_1_disclaimer => { root: 'text', count: 1, suffix: 'disclaimer' }.
 */
export type AssetNameComponents = {
    root: string;
    count: number;
    suffix: string;
};

/**
 * Parses an asset name into its component parts.
 */
export function assetNameComponentsFromName(name: string): AssetNameComponents {
    const isNumber = (parts: string[], index: number) => {
        return !isNaN(Number.parseInt(parts[index]));
    };

    const suffixes = [AssetSuffix.DisableSwap, AssetSuffix.Disclaimer, AssetSuffix.EmbeddedLogo];
    const isSuffix = (parts: string[], index: number) => {
        return suffixes.includes(parts[index] as AssetSuffix);
    };

    const parseRoot = (parts: string[], nextToken: number) => {
        // It's assumed that the first part will be a root part, even if it has a suffix name.
        const rootParts = [parts[nextToken]];
        nextToken += 1;
        while (nextToken < parts.length) {
            if (!isSuffix(parts, nextToken) && !isNumber(parts, nextToken)) {
                rootParts.push(parts[nextToken]);
            } else {
                break;
            }
            nextToken += 1;
        }

        return {
            root: rootParts.join('_'),
            nextToken,
        };
    };

    const parseCount = (parts: string[], nextToken: number) => {
        if (nextToken === parts.length) {
            return {
                count: 0,
                nextToken,
            };
        }

        const result = Number.parseInt(parts[nextToken]);

        if (!isNaN(result)) {
            nextToken += 1;
        }

        if (result < 0) {
            throw new Error('Asset name count must be >= 0');
        }

        return {
            count: result || 0,
            nextToken,
        };
    };

    const parseSuffix = (parts: string[], nextToken: number) => {
        if (nextToken === parts.length) {
            return {
                suffix: '',
                nextToken,
            };
        }

        const suffixParts = parts.slice(nextToken);

        return {
            suffix: suffixParts.join('_'),
            nextToken: parts.length,
        };
    };

    const tokens = name.split('_');
    let nextToken = 0;

    let root: string;
    let count: number;
    let suffix: string;

    ({ root, nextToken } = parseRoot(tokens, nextToken));
    ({ count, nextToken } = parseCount(tokens, nextToken));
    ({ suffix, nextToken } = parseSuffix(tokens, nextToken));

    if (nextToken !== tokens.length) {
        throw new Error(`Invalid name '${name}'`);
    }

    return {
        root,
        count,
        suffix,
    };
}

/**
 * Composes the asset name from its component parts.
 */
export function assetNameFromNameComponents(components: AssetNameComponents) {
    const result: (string | number)[] = [components.root];

    if (components.count > 0) {
        result.push(components.count);
    }

    if (components.suffix.length > 0) {
        result.push(components.suffix);
    }

    return result.join('_');
}

/**
 * Zonejs does not patch all promise like apis
 * Use this method instead of `instanceof Promise`
 */
export function isPromiseLike<T>(value: unknown): value is Promise<T> {
    return (
        value !== null &&
        typeof value === 'object' &&
        'then' in value &&
        typeof value.then === 'function' &&
        'catch' in value &&
        typeof value.catch === 'function'
    );
}
